import { create } from 'zustand';
import {
  fetchCategories as loadCategories,
  fetchProducts as loadProducts,
} from '../services/catalog.service';
import { Category, Product } from '../types/catalog';

export type QuickCategorySlug = 'all' | 'bouquets' | 'gifts' | 'flowers';

interface CatalogRequestParams {
  categoryId?: number;
  categorySlug?: string;
  search: string;
  minPrice?: number;
  maxPrice?: number;
  inStockOnly: boolean;
  sort: 'default' | 'price_asc' | 'price_desc' | 'newest' | 'oldest';
  page: number;
  limit: number;
}

const CATALOG_PAGE_SIZE = 20;

function getCurrentParams(
  get: () => CatalogState,
  overrides?: { search?: string; page?: number }
): CatalogRequestParams {
  const state = get();
  const categorySlug = state.selectedCategorySlug === 'all' ? undefined : state.selectedCategorySlug;
  const search =
    overrides?.search !== undefined ? overrides.search : (state.searchQueryForRequest ?? state.searchQuery);
  return {
    categoryId: state.selectedCategoryId,
    categorySlug,
    search,
    minPrice: state.minPrice,
    maxPrice: state.maxPrice,
    inStockOnly: state.inStockOnly,
    sort: state.sort,
    page: overrides?.page ?? 1,
    limit: CATALOG_PAGE_SIZE,
  };
}

interface CatalogState {
  products: Product[];
  categories: Category[];
  selectedCategoryId?: number;
  selectedCategorySlug: QuickCategorySlug;
  searchQuery: string;
  /** Поисковая строка, использованная при последнем запросе (для подгрузки следующих страниц). */
  searchQueryForRequest: string;
  minPrice?: number;
  maxPrice?: number;
  inStockOnly: boolean;
  /** default = order from admin; other options for optional user sort */
  sort: 'default' | 'price_asc' | 'price_desc' | 'newest' | 'oldest';
  loading: boolean;
  loadingMore: boolean;
  page: number;
  hasMore: boolean;
  error?: string;
  lastFetchedAt: number | null;
  fetchCategories: () => Promise<void>;
  fetchProducts: (overrides?: { search?: string }) => Promise<void>;
  fetchMoreProducts: () => Promise<void>;
  setCategory: (categoryId?: number) => void;
  setCategorySlug: (slug: QuickCategorySlug) => void;
  setSearchQuery: (query: string) => void;
  setPriceRange: (min?: number, max?: number) => void;
  setInStockOnly: (value: boolean) => void;
  setSort: (value: 'default' | 'price_asc' | 'price_desc' | 'newest' | 'oldest') => void;
}

export const useCatalogStore = create<CatalogState>((set, get) => ({
  products: [],
  categories: [],
  selectedCategorySlug: 'all',
  searchQuery: '',
  searchQueryForRequest: '',
  minPrice: undefined,
  maxPrice: undefined,
  inStockOnly: false,
  sort: 'default',
  loading: false,
  loadingMore: false,
  page: 1,
  hasMore: true,
  error: undefined,
  lastFetchedAt: null,

  async fetchCategories() {
    const categories = await loadCategories();
    set({ categories });
  },

  async fetchProducts(overrides?: { search?: string }) {
    const currentParams = getCurrentParams(get, { ...overrides, page: 1 });
    set({
      loading: true,
      error: undefined,
      page: 1,
      hasMore: true,
      products: [],
      searchQueryForRequest: currentParams.search,
    });

    try {
      const { products, pagination } = await loadProducts({
        categoryId: currentParams.categoryId,
        categorySlug: currentParams.categorySlug,
        search: currentParams.search,
        minPrice: currentParams.minPrice,
        maxPrice: currentParams.maxPrice,
        inStock: currentParams.inStockOnly ? true : undefined,
        sort: currentParams.sort,
        page: 1,
        limit: CATALOG_PAGE_SIZE,
      });
      const totalPages = pagination?.totalPages ?? 1;
      set({
        products,
        page: 1,
        hasMore: totalPages > 1,
        lastFetchedAt: Date.now(),
        error: undefined,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось загрузить товары';
      const finalMessage = message || 'Не удалось загрузить товары';
      set({ error: finalMessage, hasMore: false });
    } finally {
      set({ loading: false });
    }
  },

  async fetchMoreProducts() {
    const state = get();
    if (state.loadingMore || !state.hasMore) return;
    const nextPage = state.page + 1;
    const currentParams = getCurrentParams(get, { page: nextPage });
    set({ loadingMore: true });

    try {
      const { products: newProducts, pagination } = await loadProducts({
        categoryId: currentParams.categoryId,
        categorySlug: currentParams.categorySlug,
        search: currentParams.search,
        minPrice: currentParams.minPrice,
        maxPrice: currentParams.maxPrice,
        inStock: currentParams.inStockOnly ? true : undefined,
        sort: currentParams.sort,
        page: nextPage,
        limit: CATALOG_PAGE_SIZE,
      });
      const totalPages = pagination?.totalPages ?? 1;
      set({
        products: [...state.products, ...newProducts],
        page: nextPage,
        hasMore: nextPage < totalPages,
        error: undefined,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось подгрузить товары';
      set({ error: message });
    } finally {
      set({ loadingMore: false });
    }
  },

  setCategory(categoryId) {
    set({ selectedCategoryId: categoryId });
  },

  setCategorySlug(slug) {
    set({ selectedCategorySlug: slug });
  },

  setSearchQuery(query) {
    set({ searchQuery: query });
    // searchQueryForRequest обновится при следующем fetchProducts (через debounce)
  },

  setPriceRange(min, max) {
    set({ minPrice: min, maxPrice: max });
  },

  setInStockOnly(value) {
    set({ inStockOnly: value });
  },

  setSort(value) {
    set({ sort: value });
  },
}));
